import React, { useRef, useState, useEffect } from "react";
import styles from "./OrderAssistantSandbox.module.scss";
import axiosInstance from "../../../core/services/axiosConfig";

interface Message {
    sender: "user" | "bot";
    text: string;
    time?: string;
}

const OrderAssistantSandbox: React.FC = () => {
    const [userId, setUserId] = useState("");
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, loading]);

    const getTime = () => {
        const d = new Date();
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const startSession = async () => {
        if (!userId) return;
        setSessionLoading(true);
        try {
            const res = await axiosInstance.post("http://localhost:5000/api/order-chatbot-v2/session/start", {
                user_id: userId,
            });
            setSessionId(res.data.data.session_id);
            setMessages([
                { sender: "bot", text: "Session started. You can now chat with the order assistant.", time: getTime() },
            ]);
        } catch (e) {
            setMessages([{ sender: "bot", text: "Failed to start session.", time: getTime() }]);
        }
        setSessionLoading(false);
    };

    const sendMessage = async () => {
        if (!input.trim() || !sessionId) return;
        const userMsg = { sender: "user" as const, text: input, time: getTime() };
        setMessages((msgs) => [...msgs, userMsg]);
        setLoading(true);
        setInput("");
        try {
            const res = await axiosInstance.post("http://localhost:5000/api/order-chatbot-v2/session/message", {
                session_id: sessionId,
                user_id: userId,
                message: input,
            });
            setMessages((msgs) => [
                ...msgs,
                { sender: "bot", text: res.data.data.reply, time: getTime() },
            ]);
        } catch (e) {
            setMessages((msgs) => [
                ...msgs,
                { sender: "bot", text: "Failed to send message.", time: getTime() },
            ]);
        }
        setLoading(false);
    };

    return (
        <div className={styles["chatbot-container"]}>
            <div className={styles["chatbot-header"]}>Order Assistant Sandbox</div>
            {!sessionId && (
                <div style={{ padding: 24, textAlign: "center" }}>
                    <input
                        type="text"
                        placeholder="Enter User ID"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className={styles["chatbot-input"]}
                        disabled={sessionLoading}
                        style={{ width: "70%", marginBottom: 12 }}
                    />
                    <button
                        className={styles["chatbot-send-btn"]}
                        onClick={startSession}
                        disabled={sessionLoading || !userId}
                        style={{ borderRadius: 20, width: 100, height: 40, fontWeight: 600, fontSize: 16 }}
                    >
                        {sessionLoading ? (
                            <span className={styles.loader}>
                                <span className={styles["loader-dot"]}></span>
                                <span className={styles["loader-dot"]}></span>
                                <span className={styles["loader-dot"]}></span>
                            </span>
                        ) : "Start Chat"}
                    </button>
                </div>
            )}
            {sessionId && (
                <>
                    <div className={styles["chatbot-messages"]}>
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={
                                    styles["message-row"] + " " +
                                    (msg.sender === "user" ? styles["user"] : styles["bot"])
                                }
                            >
                                <div className={
                                    styles["message-bubble"] + " " +
                                    (msg.sender === "user" ? styles["user"] : styles["bot"])
                                }>
                                    {msg.text}
                                </div>
                                <span className={styles["message-time"]}>{msg.time}</span>
                            </div>
                        ))}
                        {loading && (
                            <div className={styles["message-row"] + " " + styles["bot"]}>
                                <div className={styles["message-bubble"] + " " + styles["bot"]}>
                                    <span className={styles.loader}>
                                        <span className={styles["loader-dot"]}></span>
                                        <span className={styles["loader-dot"]}></span>
                                        <span className={styles["loader-dot"]}></span>
                                    </span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                    <form
                        className={styles["chatbot-input-bar"]}
                        onSubmit={e => { e.preventDefault(); if (!loading) sendMessage(); }}
                    >
                        <input
                            type="text"
                            placeholder="Type your message..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            className={styles["chatbot-input"]}
                            disabled={loading}
                            onKeyDown={(e) => { if (e.key === "Enter" && !loading) sendMessage(); }}
                        />
                        <button
                            type="submit"
                            className={styles["chatbot-send-btn"]}
                            disabled={loading || !input.trim()}
                            aria-label="Send"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                        </button>
                    </form>
                </>
            )}
        </div>
    );
};

export default OrderAssistantSandbox;
